import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { ArrowRight, Mail, Menu, Phone, X } from "lucide-react";
import { navigationItems, siteMeta } from "@/content/siteContent";

const linkBaseClass =
  "rounded-sm px-3 py-2 text-sm font-semibold uppercase tracking-[0.18em] transition-colors duration-200";

const getNavSlug = (label) => label.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export default function SiteLayout() {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <div className="App page-shell bg-white text-slate-900">
      <div className="border-b border-slate-200 bg-slate-950 text-white">
        <div className="mx-auto flex max-w-7xl flex-col gap-2 px-4 py-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-3 sm:px-6 sm:py-3 sm:text-xs sm:uppercase sm:tracking-[0.2em] md:px-12">
          <div className="hidden items-center gap-3 sm:flex" data-testid="topbar-brand-block">
            <span className="inline-flex h-2 w-2 rounded-full bg-orange-600" />
            <span data-testid="topbar-brand-text">Food Service Industry Consulting</span>
          </div>
          <div className="flex items-center justify-between gap-3 text-sm text-slate-200 sm:flex-wrap sm:justify-end sm:gap-4 sm:text-xs">
            <a
              href={`tel:${siteMeta.phone}`}
              className="inline-flex items-center gap-2 transition-colors hover:text-white"
              data-testid="topbar-phone-link"
            >
              <Phone className="h-3.5 w-3.5 shrink-0" />
              {siteMeta.phone}
            </a>
            <a
              href={`mailto:${siteMeta.email}`}
              className="inline-flex items-center gap-2 truncate transition-colors hover:text-white"
              data-testid="topbar-email-link"
            >
              <Mail className="h-3.5 w-3.5 shrink-0" />
              <span className="max-w-[180px] truncate sm:max-w-none">{siteMeta.email}</span>
            </a>
          </div>
        </div>
      </div>

      <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-12">
          <Link
            to="/"
            className="inline-flex items-center gap-3"
            data-testid="site-logo-link"
          >
            <img
              alt="Concept Magik logo"
              className="h-10 w-auto max-w-[180px] object-contain md:h-16 md:max-w-none"
              data-testid="site-logo-image"
              src={siteMeta.logoImage}
            />
          </Link>

          <nav className="hidden items-center gap-2 lg:flex" data-testid="desktop-navigation">
            {navigationItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-link-${getNavSlug(item.label)}`}
                className={({ isActive }) =>
                  `${linkBaseClass} ${
                    isActive ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100 hover:text-slate-950"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-sm bg-orange-600 px-5 py-3 text-sm font-bold uppercase tracking-[0.18em] text-white transition-all duration-200 hover:bg-orange-500"
              data-testid="nav-contact-cta-link"
            >
              Start a conversation
              <ArrowRight className="h-4 w-4" />
            </Link>
          </nav>

          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            className="inline-flex items-center justify-center rounded-sm border border-slate-300 p-3 text-slate-900 lg:hidden"
            data-testid="mobile-menu-toggle-button"
            aria-label="Toggle menu"
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {menuOpen ? (
          <div className="border-t border-slate-200 bg-white lg:hidden" data-testid="mobile-navigation-panel">
            <div className="mx-auto flex max-w-7xl flex-col px-6 py-4 md:px-12">
              {navigationItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={`mobile-nav-link-${getNavSlug(item.label)}`}
                  className={({ isActive }) =>
                    `${linkBaseClass} my-1 ${isActive ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"}`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                className="mt-3 inline-flex items-center justify-center gap-2 rounded-sm bg-orange-600 px-5 py-3 text-sm font-bold uppercase tracking-[0.18em] text-white"
                data-testid="mobile-contact-cta-link"
              >
                Start a conversation
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        ) : null}
      </header>

      <main data-testid="site-main-content">
        <Outlet />
      </main>

      <footer className="bg-slate-950 text-white">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 py-20 md:grid-cols-[1.3fr_0.7fr] md:px-12">
          <div className="space-y-6">
            <div className="inline-flex items-center gap-3 text-sm uppercase tracking-[0.24em] text-orange-400" data-testid="footer-kicker">
              <span className="inline-flex h-2 w-2 rounded-full bg-orange-500" />
              Build a stronger food service operation
            </div>
            <h2 className="max-w-3xl text-4xl font-bold tracking-tight text-white sm:text-5xl" data-testid="footer-heading">
              Bring sharper thinking to your next hospitality project.
            </h2>
            <p className="max-w-2xl text-base text-slate-300 md:text-lg" data-testid="footer-description">
              Concept Magik combines business and design consulting to help food service leaders move with more confidence.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-sm border border-orange-500 bg-orange-600 px-6 py-4 text-sm font-bold uppercase tracking-[0.18em] text-white transition-colors hover:bg-orange-500"
              data-testid="footer-primary-cta-link"
            >
              Contact Concept Magik
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="grid gap-8 text-sm text-slate-300 sm:grid-cols-2 md:grid-cols-1">
            <div>
              <h3 className="mb-4 text-sm font-bold uppercase tracking-[0.24em] text-white" data-testid="footer-contact-heading">
                Contact
              </h3>
              <div className="space-y-3">
                <a
                  href={`tel:${siteMeta.phone}`}
                  className="block transition-colors hover:text-white"
                  data-testid="footer-phone-link"
                >
                  {siteMeta.phone}
                </a>
                <a
                  href={`mailto:${siteMeta.email}`}
                  className="block transition-colors hover:text-white"
                  data-testid="footer-email-link"
                >
                  {siteMeta.email}
                </a>
              </div>
            </div>

            <div>
              <h3 className="mb-4 text-sm font-bold uppercase tracking-[0.24em] text-white" data-testid="footer-navigation-heading">
                Explore
              </h3>
              <div className="grid gap-2">
                {navigationItems.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className="transition-colors hover:text-white"
                    data-testid={`footer-link-${getNavSlug(item.label)}`}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
